import { useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronsUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRankApartmentUp, useRankApartmentDown, useRankApartmentToTop } from '../hooks/useApartments';
import { is404, is422 } from '../utils/errors';

interface ApartmentRankControlsProps {
  apartmentId: string;
  viewOrder: number;
  isFirst: boolean;
  isLast: boolean;
  isRankingDisabled?: boolean;
  onError: (message: string) => void;
}

export const ApartmentRankControls = ({
  apartmentId,
  isFirst,
  isLast,
  isRankingDisabled = false,
  onError,
}: ApartmentRankControlsProps) => {
  const { t } = useTranslation(['pages', 'errors']);
  const queryClient = useQueryClient();

  const rankUpMutation = useRankApartmentUp();
  const rankDownMutation = useRankApartmentDown();
  const rankToTopMutation = useRankApartmentToTop();

  const anyPending =
    rankUpMutation.isPending ||
    rankDownMutation.isPending ||
    rankToTopMutation.isPending;

  const allDisabled = isRankingDisabled || anyPending;

  const handleError = (err: unknown) => {
    if (is422(err)) {
      // Silent boundary guard — just refresh the list
      queryClient.invalidateQueries({ queryKey: ['apartments'] });
      return;
    }
    if (is404(err)) {
      onError(t('errors:apartmentNotFound'));
    } else {
      onError(t('errors:somethingWentWrong'));
    }
  };

  const handleRankUp = async () => {
    try {
      await rankUpMutation.mutateAsync(apartmentId);
    } catch (err) {
      // Check for 409 specifically
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        onError(t('errors:rankingConflict'));
        queryClient.invalidateQueries({ queryKey: ['apartments'] });
      } else {
        handleError(err);
      }
    }
  };

  const handleRankDown = async () => {
    try {
      await rankDownMutation.mutateAsync(apartmentId);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        onError(t('errors:rankingConflict'));
        queryClient.invalidateQueries({ queryKey: ['apartments'] });
      } else {
        handleError(err);
      }
    }
  };

  const handleRankToTop = async () => {
    try {
      await rankToTopMutation.mutateAsync(apartmentId);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        onError(t('errors:rankingConflict'));
        queryClient.invalidateQueries({ queryKey: ['apartments'] });
      } else {
        handleError(err);
      }
    }
  };

  const btnBase =
    'flex items-center justify-center w-8 h-8 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
  const btnStyle = `${btnBase} bg-gray-50 text-gray-600 hover:bg-gray-100`;

  return (
    <div className="flex items-center gap-1" aria-label="Ranking controls">
      <button
        type="button"
        onClick={handleRankUp}
        disabled={allDisabled || isFirst}
        aria-label={t('pages:rankUp')}
        title={t('pages:rankUp')}
        className={btnStyle}
      >
        {rankUpMutation.isPending ? (
          <span className="inline-block w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <ChevronUp size={14} />
        )}
      </button>

      <button
        type="button"
        onClick={handleRankToTop}
        disabled={allDisabled || isFirst}
        aria-label={t('pages:rankToTop')}
        title={t('pages:rankToTop')}
        className={btnStyle}
      >
        {rankToTopMutation.isPending ? (
          <span className="inline-block w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <ChevronsUp size={14} />
        )}
      </button>

      <button
        type="button"
        onClick={handleRankDown}
        disabled={allDisabled || isLast}
        aria-label={t('pages:rankDown')}
        title={t('pages:rankDown')}
        className={btnStyle}
      >
        {rankDownMutation.isPending ? (
          <span className="inline-block w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <ChevronDown size={14} />
        )}
      </button>
    </div>
  );
};
